import React, { useState, useRef, useCallback } from "react";
import { Box, Text, UnstyledButton, LoadingOverlay, Image, Group } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import Swal from "sweetalert2";

import { uploadFileToS3 } from "../../../../../services/s3Service";

export default function FilterFileInput({ value, type, color, filterFormat, isEditMode, onChange, onClickRemove }) {
  const isFile = filterFormat === "file";
  const _color = color || "gray";
  const _value = typeof value === "string" && value.length > 0 ? value : null;

  const [isUploading, setIsUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const inputRef = useRef(null);

  const handleFileChange = useCallback(async (event) => {
    const _file = event.target.files[0];
    if (!_file) return;

    setIsUploading(true);
    
    try {
      const fileUrl = await uploadFileToS3(
        window?.$RAILS_ENV?.S3_ACCESS_KEY,
        window?.$RAILS_ENV?.S3_SECRET_KEY,
        _file, {
          folder: 'uploads',
          onProgress: (progress) => {
            console.log(`Upload progress: ${progress}%`);
          }
        }
      );

      if (typeof onChange === 'function') {
        onChange(fileUrl);
      }
      
    } catch (error) {
      console.log('Error uploading file:', error);
      Swal.fire("Uploading error.", error?.message || "Something went wrong", "error");
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  }, [onChange]);

  const onDelete = async () => {
    if (typeof onClickRemove === 'function') {
      onClickRemove();
    }
  };

  const onDownload = () => {
    if (typeof _value === "string") {
      const link = document.createElement("a");
      link.href = _value;
      link.download = _value.split("/").pop();
      link.click();
    }
  }

  return (
    <Box style={{ position: 'relative' }} mt={10}>
      <input
        ref={inputRef}
        type="file"
        accept={isFile
          ? ".mp4,.avi,.mkv,.webm,.mov,.yuv,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,audio/*,image/*"
          : "image/*"
        }
        style={{ display: "none" }}
        onChange={handleFileChange}
        disabled={isUploading}
      />

      {typeof _value === "string" && (
        <Box mb={10}>
          {isFile ? (
            <Group>
              <FontAwesomeIcon icon={solid("file")} color={_color} size="2x" />
              <Text
                size="sm"
                color={_color}
                underline={!isEditMode}
                onClick={!isEditMode ? onDownload : undefined}
                style={{ cursor: isEditMode ? "default" : "pointer", flex: 1 }}
              >
                {_value.split("/").pop()}
              </Text>
              {isEditMode && (
                <UnstyledButton onClick={onDelete}>
                  <FontAwesomeIcon icon={solid("circle-xmark")} color={_color} size="xl" />
                </UnstyledButton>
              )}
            </Group>
          ) : (
            <>
              {isEditMode && (
                <UnstyledButton style={{ position: "absolute", zIndex: 1, top: 10, right: 10 }} onClick={onDelete}>
                  <FontAwesomeIcon icon={solid("circle-xmark")} color="gray" size="2x" />
                </UnstyledButton>
              )}
              <Image radius="md" src={_value} />
            </>
          )}
        </Box>
      )}

      {isEditMode && (
        <UnstyledButton
          onClick={(e) => {
            if (isEditMode && typeof inputRef.current?.click === "function") {
              e.preventDefault();
              e.stopPropagation();
              window.$tiptapSidemenu = true;
              setDialogOpen(true);
              inputRef.current.click();
              setTimeout(() => {
                window.$tiptapSidemenu = false;
              }, 1000);
            }
          }}
          disabled={isUploading}
          style={{
            border: '1px dashed #ced4da',
            borderRadius: '4px',
            padding: '16px',
            width: '100%',
            textAlign: 'center',
            cursor: isUploading ? 'wait' : 'pointer',
            opacity: isUploading ? 0.7 : 1
          }}
        >
          <LoadingOverlay visible={isUploading} overlayBlur={1} radius="md" />
          <Text size="sm" color="grey">
            {isUploading
              ? "Uploading..."
              : `Click to ${typeof _value === "string" ? "change" : "upload"} ${isFile ? "file" : "image"}`
            }
          </Text>
        </UnstyledButton>
      )}

    </Box>
  );
}